import React, { useState } from 'react';

import { Table } from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import VpnKey from '@material-ui/icons/VpnKey';
import ShareIcon from '@material-ui/icons/Share';

import { StorageContainer, useStoragePermissions } from '../hooks';
import { GenerateTokenDialog, CopyTokenDialog } from '../TokenDialog';
import { useContainerAccount, ContainerSAS } from './useContainerAccount';
import { useNavigate } from 'react-router-dom';

interface CardStorageContainersProps {
  data?: StorageContainer[];
  name: string;
  namespace: string;
  stgAccountEntity?: string;
}

const tableStyle: React.CSSProperties = {
  minWidth: '0',
  width: '100%',
  height: 'calc(100% - 10px)',
};

const actionsCellStyle: React.CSSProperties = {
  padding: '0px 24px',
};

export const CardStorageContainers = ({
  data = [],
  name,
  namespace,
  stgAccountEntity,
}: CardStorageContainersProps) => {
  const [openGenerateTokenDialog, setOpenGenerateTokenDialog] = useState(false);
  const [openCopyTokenDialog, setOpenCopyTokenDialog] = useState(false);
  const [sasToken, setSasToken] = useState<ContainerSAS>();
  const [container, setContainer] = useState<StorageContainer>();
  const [tokenDuration, setTokenDuration] = useState('');
  const navigate = useNavigate();

  const { generateSASToken } = useContainerAccount({ name, namespace });
  const alertApi = useApi(alertApiRef);
  const { hasContainerSASPermission } = useStoragePermissions();

  const generateStorageContainerSASToken = async (
    duration: number,
    durationText: string,
  ) => {
    try {
      const sasTokenResponse = await generateSASToken(
        container?.name || '',
        duration,
      );
      setSasToken(sasTokenResponse);
      setTokenDuration(durationText);

      setOpenCopyTokenDialog(true);
      setOpenGenerateTokenDialog(false);
    } catch (e: any) {
      alertApi.post({
        message: `Error on generate container sas token: ${e?.message}`,
        severity: 'error',
      });
    }
  };

  const columns = [{ title: 'Name', field: 'name' }];

  const actions = hasContainerSASPermission
    ? [
        (storageContainer: StorageContainer) => ({
          icon: () => <VpnKey fontSize="small" />,
          tooltip: 'Generate SAS Token for container',
          onClick: () => {
            setContainer(storageContainer);
            setOpenGenerateTokenDialog(true);
          },
        }),
        (storageContainer: StorageContainer) => ({
          icon: () => <ShareIcon fontSize="small" />,
          tooltip: 'Generate SAS Token for another system',
          onClick: () => {
            const params = new URLSearchParams();
            params.set(
              'formData',
              JSON.stringify({
                storageaccount: stgAccountEntity,
                container: storageContainer?.name,
              }),
            );
            navigate(
              `/create/templates/default/storageaccount-container-sas?${params.toString()}`,
            );
          },
        }),
      ]
    : [];

  return (
    <>
      <Table
        title="Containers"
        columns={columns}
        data={data}
        actions={actions}
        style={tableStyle}
        options={{
          search: false,
          paging: false,
          loadingType: 'linear',
          maxBodyHeight: 500,
          actionsCellStyle,
        }}
      />

      <GenerateTokenDialog
        name={`storage container ${container?.name}`}
        open={openGenerateTokenDialog}
        handleClick={generateStorageContainerSASToken}
        handleClose={() => setOpenGenerateTokenDialog(false)}
      />

      <CopyTokenDialog
        name={`storage container ${container?.name}`}
        token={sasToken?.sas || ''}
        urls={sasToken?.urls}
        tokenDuration={tokenDuration}
        open={openCopyTokenDialog}
        handleClose={() => setOpenCopyTokenDialog(false)}
      />
    </>
  );
};
